import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { companyStats } from "../data/site";

function Counter({ value, suffix }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-15%" });
    const [display, setDisplay] = useState(0);
    useEffect(() => {
        if (!inView) return;
        const start = performance.now();
        const dur = 1800;
        let raf;
        const step = (now) => {
            const p = Math.min(1, (now - start) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setDisplay(Math.round(value * eased));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [inView, value]);
    return (
        <span ref={ref}>
            {display}
            {suffix}
        </span>
    );
}

export default function Stats() {
    return (
        <section
            id="stats"
            className="relative bg-[#0b0b0b] py-24 md:py-32 px-6 md:px-10 border-y border-[rgba(212,175,55,0.18)]"
            data-testid="stats-section"
        >
            {/* Faint watermark word */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden"
            >
                <span className="font-display text-[22vw] text-white/[0.025] tracking-tighter select-none whitespace-nowrap">
                    LEGACY
                </span>
            </div>

            <div className="mx-auto max-w-[1440px] relative">
                <div className="flex items-center gap-6 mb-10">
                    <span className="w-16 h-px bg-[#C89B3C]" />
                    <span className="text-[10px] uppercase tracking-[0.4em] text-[#C89B3C]">
                        Chapter 06 — By the Numbers
                    </span>
                </div>

                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-15%" }}
                    transition={{ duration: 0.9 }}
                    className="font-display font-light text-white text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight max-w-3xl"
                >
                    Four decades. One <em className="text-[#E2C56D]">standard.</em>
                </motion.h2>

                <div className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-4 border-t border-[rgba(212,175,55,0.2)]">
                    {companyStats.map((s, i) => (
                        <motion.div
                            key={s.label}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{ duration: 0.7, delay: i * 0.1 }}
                            className={`py-10 md:py-16 px-4 md:px-6 ${
                                i !== companyStats.length - 1
                                    ? "md:border-r md:border-[rgba(212,175,55,0.18)]"
                                    : ""
                            } border-b md:border-b-0 border-[rgba(212,175,55,0.18)]`}
                            data-testid={`stat-${i}`}
                        >
                            <div className="text-[9px] uppercase tracking-[0.4em] text-[#7a7a7a] mb-4">
                                / {String(i + 1).padStart(2, "0")}
                            </div>
                            <div className="font-display font-light text-[#E2C56D] text-6xl md:text-7xl tracking-tighter leading-none">
                                <Counter value={s.value} suffix={s.suffix} />
                            </div>
                            <div className="mt-5 text-[11px] uppercase tracking-[0.28em] text-[#bebebe]">
                                {s.label}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
